import { useMemo } from 'react';
import { DateTime, StringUnitLength } from 'luxon';

import { DateString } from 'types/support';
import useCurrentUser from './use-current-user';
import { parseUtcDate, utcToLocalDate } from '~/utils/date';

export type DateInput =
  | DateTime
  | string
  | Date
  | number
  | null
  | undefined
  | DateString;

export interface DateHookOptions {
  relativeStyle: StringUnitLength;
  parseNumberAs: 'seconds' | 'millis';
}

export interface DateHookResult<DateInput, DateOutput, RelativeOutput> {
  original: DateInput;
  utc: DateOutput;
  local: DateOutput;
  relative: RelativeOutput;
}

type DateHookStringResult = DateHookResult<string, DateTime, string>;
type DateHookNumberResult = DateHookResult<number, DateTime, string>;
type DateHookDateResult = DateHookResult<Date, DateTime, string>;
type DateHookUndefinedResult = DateHookResult<undefined, undefined, undefined>;
type DateHookNullResult = DateHookResult<null, undefined, undefined>;
type DateHookDateTimeResult = DateHookResult<DateTime, DateTime, string>;

export type DateHookResultObject =
  | DateHookStringResult
  | DateHookNumberResult
  | DateHookDateResult
  | DateHookUndefinedResult
  | DateHookNullResult
  | DateHookDateTimeResult;

export default function useDate(
  input: string | number | Date,
  options?: Partial<DateHookOptions>
): DateHookStringResult | DateHookNumberResult | DateHookDateResult;
export default function useDate(
  input: string | number | Date | null | undefined,
  options?: Partial<DateHookOptions>
):
  | DateHookStringResult
  | DateHookNumberResult
  | DateHookDateResult
  | DateHookUndefinedResult
  | DateHookUndefinedResult;
export default function useDate(
  input: DateTime,
  options?: Partial<DateHookOptions>
): DateHookDateTimeResult;
export default function useDate(
  input: string,
  options?: Partial<DateHookOptions>
): DateHookStringResult;
export default function useDate(
  input: number,
  options?: Partial<DateHookOptions>
): DateHookNumberResult;
export default function useDate(
  input: Date,
  options?: Partial<DateHookOptions>
): DateHookDateResult;
export default function useDate(
  input: undefined,
  options?: Partial<DateHookOptions>
): DateHookUndefinedResult;
export default function useDate(
  input: null,
  options?: Partial<DateHookOptions>
): DateHookNullResult;
export default function useDate(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: any,
  options?: Partial<DateHookOptions>
): DateHookResultObject {
  const user = useCurrentUser({
    queryOptions: {
      enabled: false,
    },
  });

  const utc = useMemo<DateTime | undefined>(() => {
    if (DateTime.isDateTime(input)) {
      return input.toUTC();
    }

    return parseUtcDate(input, { parseNumberAs: options?.parseNumberAs });
  }, [input]);

  return useMemo(() => {
    const local = utc && utcToLocalDate(utc, user.timezone);
    const relative = local?.toRelative({
      style: options?.relativeStyle ?? 'short',
    });

    return {
      original: input,
      utc,
      local,
      relative: relative ?? undefined,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, [user, input, utc]) as any;
}

export function useRelativeDate(
  input: undefined | null,
  style?: StringUnitLength
): undefined;
export function useRelativeDate(
  input: Date | DateTime | DateString,
  style?: StringUnitLength
): string;
export function useRelativeDate<
  RelativeDateString extends string | undefined = string | undefined
>(input: DateInput, style?: StringUnitLength): RelativeDateString;
export function useRelativeDate(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: any,
  style: StringUnitLength = 'short'
): string | undefined {
  const { relative } = useDate(input, { relativeStyle: style });

  return relative;
}

export function useDateFormat(
  input: null | undefined,
  format: string,
  parseNumberAs?: 'millis' | 'seconds'
): undefined;
export function useDateFormat(
  input: string | number | Date | DateTime,
  format: string,
  parseNumberAs?: 'millis' | 'seconds'
): string;
export function useDateFormat(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: any,
  format: string,
  parseNumberAs?: 'millis' | 'seconds'
): string | undefined {
  const { local } = useDate(input, { parseNumberAs });

  if (local) {
    return local.toFormat(format);
  }
}
